/**
 * Public API Client
 *
 * API methods for public endpoints (no authentication required).
 * Used for navigation tools dropdown and other public features.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.dev.hfu.digital';

// ============================================================================
// TYPES
// ============================================================================

export interface PublicTool {
    id: string;
    name: string;
    displayName: string;
    shortDescription?: string;
    description?: string;
    url?: string;
    iconName?: string;
    category?: string;
    isFeatured: boolean;
}

// ============================================================================
// TOOLS
// ============================================================================

/**
 * Get all public tools (for navigation dropdown)
 * Returns empty array on failure since this is non-critical UI data
 */
export async function getPublicTools(): Promise<PublicTool[]> {
    try {
        const response = await fetch(`${API_URL}/public/tools`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.warn(`Failed to fetch tools: ${response.status}`);
            return [];
        }

        return response.json();
    } catch (error) {
        // Network errors, CORS issues, or API unavailable - fail silently
        console.warn('Could not load tools:', error instanceof Error ? error.message : 'Unknown error');
        return [];
    }
}

// ============================================================================
// BUG REPORTS
// ============================================================================

interface SubmitBugReportOptions {
    description: string;
    screenshot: Blob | null;
}

/**
 * Submit a bug report to the API
 */
export async function submitBugReport({ description, screenshot }: SubmitBugReportOptions): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
        const formData = new FormData();
        formData.append('description', description);
        formData.append('sourceApp', 'better_splan');
        formData.append('pageUrl', window.location.href);
        formData.append('pageRoute', window.location.pathname);
        formData.append('browserInfo', getBrowserInfo());
        formData.append('userAgent', navigator.userAgent);
        formData.append('screenResolution', `${screen.width}x${screen.height}`);
        formData.append('viewportSize', `${window.innerWidth}x${window.innerHeight}`);

        if (screenshot) {
            formData.append('screenshot', screenshot, 'screenshot.jpg');
        }

        const response = await fetch(`${API_URL}/bug-reports`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
            signal: controller.signal,
        });

        clearTimeout(timeoutId);
        return response.ok;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
            console.error('Bug report submission timed out');
        } else {
            console.error('Bug report submission failed:', error);
        }
        return false;
    }
}

function getBrowserInfo(): string {
    const ua = navigator.userAgent;
    let browser = 'Unknown';

    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';

    return browser;
}
